// Cinematic simulation script. Every token, number and return below is SYNTHETIC and illustrative.
export const STAGES = [
  { id: "discovery", label: "Discovery" },
  { id: "candidate", label: "Candidate" },
  { id: "hive", label: "Hive analysis" },
  { id: "verify", label: "Verification" },
  { id: "purchase", label: "Purchase" },
  { id: "monitor", label: "Monitoring" },
  { id: "exit", label: "Exit & repeat" },
];

const CHECKS = [
  { id: "identity", label: "Token & chain identity" },
  { id: "source", label: "Source authenticity" },
  { id: "controls", label: "Contract controls & wallet concentration" },
  { id: "liquidity", label: "Liquidity & executable exit route" },
  { id: "sizing", label: "Quote, position size & exposure" },
];
export const CHECK_SEC = 2.2;

const PASS_DETAIL = {
  identity: "Solana mint resolves · ticker is not a copy of a listed token",
  source: "Launch seen on the Pump.fun stream · X posts traced to original accounts",
  controls: "Mint & freeze authority revoked · top-10 wallets hold 18%",
  liquidity: "Pool $41k (floor $25k) · sell route quoted, impact 2.1%",
  sizing: "0.25 SIM-SOL · 1.2% of reserve · inside the daily exposure cap",
};

const FINDINGS = (flag) => [
  { agent: "scout", text: "Launch 4 min old · bonding curve 38% · 212 buyers" },
  { agent: "grok", text: "38 X posts in 10 min · narrative score 71 · originals, not reposts" },
  flag
    ? { agent: "tracker", text: "Buyer wallets look clustered: several funded from one source", warn: true }
    : { agent: "tracker", text: "Net buyers +146 · no wallet above 3.1%" },
  { agent: "sentinel", text: "Mint & freeze authority revoked · LP $41k" },
  { agent: "analyst", text: "Similar setups in the journal had mixed outcomes, so keep size small" },
];

export const SCENARIOS = {
  runner: {
    id: "runner", label: "Runner", blurb: "Partial profit, then a trailing exit",
    token: { sym: "NEONMOTH", mint: "SIMnM7…q4Kp", firstSeen: "T+00:04:12" },
    findings: FINDINGS(false), oracle: "Evidence assembled → advance to verification",
    checks: CHECKS.map((c) => ({ ...c, ok: true, detail: PASS_DETAIL[c.id] })),
    price: [[0, 0], [0.12, 8], [0.28, 22], [0.45, 38], [0.66, 64], [0.8, 55], [0.86, 48], [1, 48]],
    liq: [[0, 41], [0.5, 58], [0.86, 61], [1, 61]],
    events: [
      { u: 0.28, text: "TP1 reached +22% → sold 50% (simulated)", kind: "gain" },
      { u: 0.45, text: "Runner mode: trailing stop follows the high", kind: "info" },
      { u: 0.66, text: "New high +64% · trail set at 10% below", kind: "info" },
      { u: 0.86, text: "Trail hit at +48% → exit remaining 50%", kind: "exit" },
    ],
    exitAt: 0.86, exitLabel: "Trailing exit · remaining 50% at +48%",
    result: { pct: "+35%", text: "Example result: 50% at +22%, 50% at +48%", kind: "gain" },
  },
  rejected: {
    id: "rejected", label: "Rejected candidate", blurb: "Fails a risk check, no purchase",
    token: { sym: "GLASSWASP", mint: "SIMgW2…v9Rt", firstSeen: "T+00:02:47" },
    findings: FINDINGS(true), oracle: "Advance with a flag: wallet concentration",
    checks: CHECKS.map((c, i) => i === 2
      ? { ...c, ok: false, detail: "Top-10 wallets hold 46% (limit 30%) · 31 buyers funded by one wallet" }
      : { ...c, ok: true, detail: PASS_DETAIL[c.id] }),
    failAt: 2,
    result: { pct: "No trade", text: "Purchase blocked at verification. No capital used", kind: "neutral" },
  },
  fast: {
    id: "fast", label: "Fast mover", blurb: "$1M in its first hour, sold after 60 seconds", fast: true, timer: 60,
    token: { sym: "ROCKETEEL", mint: "SIMrE8…t3Zx", firstSeen: "T+00:41:09" },
    findings: [
      { agent: "scout", text: "Fast-mover feed: market cap passed $1M only 41 min after its first pool" },
      { agent: "grok", text: "X mentions accelerating · 120 posts in 15 min" },
      { agent: "tracker", text: "$640k bought in the last hour · buyers widely spread" },
      { agent: "sentinel", text: "Pool $210k (floor $25k) · mint & freeze authority revoked" },
      { agent: "analyst", text: "Fast-mover rule: in, then out 60 seconds later whatever the price" },
    ],
    oracle: "Fast-mover rule matched → priority entry, 60 s hold",
    checks: CHECKS.map((c) => ({ ...c, ok: true, detail: c.id === "liquidity" ? "Pool $210k (floor $25k) · sell route quoted, impact 0.9%" : c.id === "sizing" ? "0.15 SIM-SOL fast-mover size · inside the daily exposure cap" : PASS_DETAIL[c.id] })),
    price: [[0, 0], [0.2, 4], [0.45, 11], [0.65, 7], [0.85, 9], [1, 9]],
    liq: [[0, 210], [0.5, 236], [1, 228]],
    events: [
      { u: 0.02, text: "60-second exit timer started at fill", kind: "info" },
      { u: 0.45, text: "T+30 s · +11% (simulated)", kind: "gain" },
      { u: 0.85, text: "T+60 s → sold 100%, whatever the price", kind: "exit" },
    ],
    exitAt: 0.85, exitLabel: "Timed exit · 100% at 60 s",
    result: { pct: "+9%", text: "Example result: sold at the 60-second mark", kind: "gain" },
    captions: {
      scan: "The fast-mover feed re-prices every new Solana pool. Anything passing $1M market cap inside its first hour jumps the queue.",
      focus: "Most launches stay small. One crosses $1M 41 minutes after its first pool and is flagged as a fast mover.",
      monitor: "Fast movers are held for 60 seconds, then sold whatever the price. Shown here compressed.",
    },
  },
  loss: {
    id: "loss", label: "Losing position", blurb: "Protective exit, recorded loss",
    token: { sym: "VELVETBUG", mint: "SIMvB5…k1Lm", firstSeen: "T+00:06:03" },
    findings: FINDINGS(false), oracle: "Evidence assembled → advance to verification",
    checks: CHECKS.map((c) => ({ ...c, ok: true, detail: PASS_DETAIL[c.id] })),
    price: [[0, 0], [0.14, 5], [0.32, -1], [0.52, -6], [0.72, -11], [1, -11]],
    liq: [[0, 41], [0.4, 37], [0.72, 31], [1, 31]],
    events: [
      { u: 0.14, text: "Early push to +5%, momentum fading", kind: "info" },
      { u: 0.42, text: "Liquidity down 12% · two large holders selling", kind: "warn" },
      { u: 0.72, text: "Protective stop triggered → exit 100%", kind: "exit" },
    ],
    exitAt: 0.72, exitLabel: "Protective exit · 100% at −11%",
    result: { pct: "−11%", text: "Example result: loss recorded, stop limited the damage", kind: "loss" },
  },
};

const BEAT = {
  scan: { stage: "discovery", dur: 7, caption: "Pump.fun launches and X conversations enter as separate streams. Scout and GrokBot sweep them for candidates." },
  focus: { stage: "discovery", dur: 5, caption: "Most activity stays in the background. Weak candidates are dismissed. One draws converging signals." },
  candidate: { stage: "candidate", dur: 6, caption: "The candidate is pulled into the inspection chamber. Verified facts are separated from what is still unresolved." },
  hive: { stage: "hive", dur: 9, caption: "Specialists converge and each sends a finding. The Oracle assembles the evidence. Agreement is not proof of profit." },
  verify: { stage: "verify", dur: CHECK_SEC * 5 + 0.6, caption: "Five sequential checks. Any failure stops the purchase. Passing is not a safety guarantee." },
  purchase: { stage: "purchase", dur: 6.5, caption: "An approved, simulated order travels the execution channel. It only counts as open once confirmation arrives." },
  monitor: { stage: "monitor", dur: 12, caption: "Price, liquidity, wallet activity and exit conditions are watched continuously." },
  exit: { stage: "exit", dur: 5.5, caption: "The exit runs through its own confirmation channel and is written to the decision journal." },
  repeat: { stage: "exit", dur: 6, caption: "Released capital returns to the reserve. Agents disperse and discovery continues." },
};

export function buildBeats(sc) {
  const ids = sc.failAt != null ? ["scan", "focus", "candidate", "hive", "verify", "repeat"] : Object.keys(BEAT);
  let t = 0;
  return ids.map((id) => {
    const b = { id, ...BEAT[id] };
    if (sc.captions?.[id]) b.caption = sc.captions[id];
    if (id === "verify" && sc.failAt != null) b.dur = CHECK_SEC * (sc.failAt + 1) + 2.4;
    if (id === "repeat" && sc.failAt != null) b.caption = "Purchase blocked. The candidate is released and the Hive returns to discovery.";
    const out = { ...b, start: t };
    t += b.dur;
    return out;
  });
}

export const totalOf = (beats) => beats[beats.length - 1].start + beats[beats.length - 1].dur;

export function locate(beats, t) {
  let i = beats.length - 1;
  while (i > 0 && t < beats[i].start) i--;
  const b = beats[i];
  return { i, beat: b, u: Math.min(1, Math.max(0, (t - b.start) / b.dur)) };
}

export function lerpSeries(pts, u) {
  if (!pts) return 0;
  for (let i = 1; i < pts.length; i++) {
    if (u <= pts[i][0]) {
      const [u0, v0] = pts[i - 1], [u1, v1] = pts[i];
      return v0 + (v1 - v0) * ((u - u0) / (u1 - u0 || 1));
    }
  }
  return pts[pts.length - 1][1];
}

// verification progress -> {idx, phase:'scan'|'pass'|'fail'}
export function checkState(sc, u, dur) {
  const t = u * dur, idx = Math.min(4, Math.floor(t / CHECK_SEC)), within = (t % CHECK_SEC) / CHECK_SEC;
  if (sc.failAt != null && idx >= sc.failAt) return { idx: sc.failAt, phase: t >= CHECK_SEC * sc.failAt + CHECK_SEC * 0.6 ? "fail" : "scan" };
  return { idx, phase: within > 0.6 || t >= CHECK_SEC * 5 ? "pass" : "scan" };
}
